import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

interface UseDismissibleOverlayOptions {
  active: boolean;
  insideRefs: Array<RefObject<HTMLElement | null>>;
  onDismiss: () => void;
  dismissOnEscape?: boolean;
}

export function useDismissibleOverlay({
  active,
  insideRefs,
  onDismiss,
  dismissOnEscape = true,
}: UseDismissibleOverlayOptions) {
  const insideRefsRef = useRef(insideRefs);
  const onDismissRef = useRef(onDismiss);

  insideRefsRef.current = insideRefs;
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!active) {
      return;
    }

    const isEventInside = (
      target: EventTarget | null,
      eventPath: EventTarget[],
    ) => {
      if (!(target instanceof Node)) {
        return false;
      }

      return insideRefsRef.current.some((ref) => {
        const node = ref.current;

        if (!node) {
          return false;
        }

        return node.contains(target) || eventPath.includes(node);
      });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (dismissOnEscape && event.key === 'Escape') {
        onDismissRef.current();
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      const eventPath =
        typeof event.composedPath === 'function' ? event.composedPath() : [];

      if (isEventInside(target, eventPath)) {
        return;
      }

      onDismissRef.current();
    };

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      const eventPath =
        typeof event.composedPath === 'function' ? event.composedPath() : [];

      if (isEventInside(target, eventPath)) {
        return;
      }

      onDismissRef.current();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [active, dismissOnEscape]);
}
